import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import DocumentsFunnel from 'modules/Documentation/DocumentsFunnel';
import { useTranslation } from 'react-i18next';
import { Radios, Field, Link, Icon } from '@ontruck/orc';
import { COUNTRIES, findCountry } from 'utils/constants/countries';
import Select from 'react-select';
import { reactSelectStyles } from 'utils/style/select';
import useDocsCarrierForm from 'hooks/useDocsCarrierForm/useDocsCarrierForm';
import cx from 'classnames';
import ButtonLoading from 'components/ButtonLoading';
import Spinner from 'components/Spinner';
import DynamicFormQuestion from 'modules/Documentation/DynamicFormQuestion';
import DocumentsFormQuestion from 'modules/Documentation/DocumentsFormQuestion';

const HelpTooltip = ({ labelText, helpText }) => {
  const [showText, setShowText] = useState(false);

  const toggleText = useCallback(() => {
    setShowText(!showText);
  }, [showText]);

  return (
    <div className="documents-billing-data__tooltip-container">
      <Link
        className={cx('documents-billing-data__tooltip-link', {
          'documents-billing-data__tooltip-link--open': showText,
        })}
        iconPosition="right"
        icon="continue"
        skin="dark"
        text={labelText}
        onClick={toggleText}
      />
      {showText && (
        <div className="documents-billing-data__tooltip-text">{helpText}</div>
      )}
    </div>
  );
};

const getIsAutonomousValue = (carrierData, serverCarrierData) => {
  if ('is_autonomous' in carrierData) {
    return carrierData.is_autonomous.toString();
  }
  if ('is_autonomous' in serverCarrierData) {
    serverCarrierData.is_autonomous.toString();
  }
  return null;
};

const DocumentsBillingData = ({ className }) => {
  const { t } = useTranslation('Documents');
  const {
    handleFieldChange,
    carrierData,
    handleSubmit,
    serverCarrierData,
    loadingProfile,
    fieldsWithError,
    loadingForm,
    countrySpecificFields,
  } = useDocsCarrierForm();
  if (loadingProfile) {
    return (
      <div className={className} data-testid="documents-billing-data">
        <div className="documents-billing-data">
          <div
            className="documents-billing-data__loading"
            data-testid="documents-billing-data__loading"
          >
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className} data-testid="documents-billing-data">
      <div className="documents-billing-data">
        <div className="documents-billing-data__header">
          <DocumentsFunnel activeStep={1} />
          <h1 className="documents-billing-data__title">{t('billingData')}</h1>
          <div
            className="documents-billing-data__warning"
            data-testId="documents-billing-data__warning"
          >
            <span className="documents-billing-data__warning--icon">
              <Icon glyph="girded-info" size="1.6rem" />
            </span>
            <span className="documents-billing-data__warning--text">
              {t('modifySavedDocs')}
            </span>
          </div>
        </div>
        <div className="documents-billing-data__container">
          <DocumentsFormQuestion
            error={t('optionsError')}
            name="is_autonomous"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Radios
              size="M"
              legend={t('typeLabel')}
              name="is_autonomous"
              items={[
                {
                  value: 'true',
                  label: t('ownerOperator'),
                },
                {
                  value: 'false',
                  label: t('others'),
                },
              ]}
              value={getIsAutonomousValue(carrierData, serverCarrierData)}
              onChange={(e, { name, value }) => {
                handleFieldChange(name, value === 'true');
              }}
            />
          </DocumentsFormQuestion>
          <DocumentsFormQuestion
            error={t('fiscalNameError')}
            name="name"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Field
              onChange={(e, { name, value }) => handleFieldChange(name, value)}
              value={serverCarrierData?.name || carrierData.name}
              name="name"
              id="name"
              label={t('fiscalName')}
              size="M"
              labelType="regular"
              disabled={!!serverCarrierData?.name}
              placeholder={t('fiscalNamePlaceholder')}
            />
          </DocumentsFormQuestion>
          <DocumentsFormQuestion
            error={t('idNumberError')}
            name="tax_number"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Field
              onChange={(e, { name, value }) => handleFieldChange(name, value)}
              value={serverCarrierData?.tax_number || carrierData.tax_number}
              name="tax_number"
              id="tax_number"
              label={t('idNumber')}
              size="M"
              labelType="regular"
              disabled={!!serverCarrierData?.tax_number}
              placeholder={t('idNumberPlaceholder')}
            />
          </DocumentsFormQuestion>
          {!carrierData.is_autonomous && !serverCarrierData.is_autonomous && (
            <DocumentsFormQuestion
              error={t('requiredFieldError')}
              name="contact_name"
              fieldsWithError={fieldsWithError}
              serverCarrierData={serverCarrierData}
            >
              <Field
                onChange={(e, { name, value }) =>
                  handleFieldChange(name, value)
                }
                value={
                  serverCarrierData?.contact_name || carrierData.contact_name
                }
                name="contact_name"
                id="contact_name"
                label={`${t('partnerName')} (${t('noRequired')})`}
                size="M"
                labelType="regular"
                disabled={!!serverCarrierData?.contact_name}
                placeholder={t('partnerNamePlaceholder')}
              />
              {!serverCarrierData?.contact_name && (
                <span className="documents-billing-data__question-help">
                  {t('partnerNameHelp')}
                </span>
              )}
            </DocumentsFormQuestion>
          )}
          <div
            className={cx(
              'documents-billing-data__question',
              'documents-billing-data__question--country',
              'documents-billing-data__question--select'
            )}
          >
            <label className="documents-billing-data__select-label">
              {t('country')}
            </label>
            <DocumentsFormQuestion
              error={t('optionsError')}
              name="fiscal_country"
              fieldsWithError={fieldsWithError}
              serverCarrierData={serverCarrierData}
            >
              <Select
                name="fiscal_country"
                options={COUNTRIES}
                placeholder={t('country')}
                styles={reactSelectStyles}
                onChange={(option) =>
                  handleFieldChange('fiscal_country', option['alpha-2'])
                }
                value={
                  findCountry(serverCarrierData?.fiscal_country) ||
                  findCountry(carrierData.fiscal_country)
                }
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o['alpha-2']}
                disabled={!!serverCarrierData?.fiscal_country}
              />
            </DocumentsFormQuestion>
          </div>
          <DocumentsFormQuestion
            error={t('zipcodeError')}
            name="zipcode"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Field
              onChange={(e, { name, value }) =>
                handleFieldChange('billing_address', {
                  ...carrierData.billing_address,
                  zipcode: value,
                })
              }
              name="zipcode"
              id="zipcode"
              label={t('zipcode')}
              size="M"
              labelType="regular"
              placeholder={t('zipcodePlaceholder')}
              value={
                serverCarrierData?.billing_address?.zipcode ||
                carrierData.billing_address?.zipcode
              }
              disabled={!!serverCarrierData?.billing_address?.zipcode}
            />
          </DocumentsFormQuestion>
          <DocumentsFormQuestion
            error={t('cityError')}
            name="city"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Field
              onChange={(e, { name, value }) =>
                handleFieldChange('billing_address', {
                  ...carrierData.billing_address,
                  city: value,
                })
              }
              name="city"
              id="city"
              label={t('city')}
              size="M"
              labelType="regular"
              placeholder={t('cityPlaceholder')}
              value={
                serverCarrierData?.billing_address?.city ||
                carrierData.billing_address?.city
              }
              disabled={!!serverCarrierData?.billing_address?.city}
            />
          </DocumentsFormQuestion>
          <DocumentsFormQuestion
            error={t('fiscalAddressError')}
            name="address_line"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Field
              onChange={(e, { name, value }) =>
                handleFieldChange('billing_address', {
                  ...carrierData.billing_address,
                  address_line: value,
                })
              }
              name="address_line"
              id="fiscal-address"
              label={t('fiscalAddress')}
              size="M"
              labelType="regular"
              placeholder={t('fiscalAddressPlaceholder')}
              value={
                serverCarrierData?.billing_address?.address_line ||
                carrierData.billing_address?.address_line
              }
              disabled={!!serverCarrierData?.billing_address?.address_line}
            />
          </DocumentsFormQuestion>
          <DocumentsFormQuestion
            error={t('ibanError')}
            name="iban"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Field
              onChange={(e, { name, value }) => handleFieldChange(name, value)}
              name="iban"
              id="iban"
              label={`IBAN (${t('noRequired')})`}
              size="M"
              labelType="regular"
              placeholder={t('ibanPlaceholder')}
              value={serverCarrierData?.iban || carrierData.iban}
              disabled={!!serverCarrierData?.iban}
            />
            {!serverCarrierData?.iban && (
              <span className="documents-billing-data__question-help">
                {t('ibanHelp')}
              </span>
            )}
          </DocumentsFormQuestion>
          <HelpTooltip
            labelText={t('ibanHelpLabel')}
            helpText={t('ibanHelpText')}
          />
          <DocumentsFormQuestion
            error={t('emailError')}
            name="billing_email"
            fieldsWithError={fieldsWithError}
            serverCarrierData={serverCarrierData}
          >
            <Field
              onChange={(e, { name, value }) => handleFieldChange(name, value)}
              name="billing_email"
              id="billing-email"
              label={`${t('billingEmail')} (${t('noRequired')})`}
              size="M"
              labelType="regular"
              placeholder={t('emailPlaceholder')}
              value={
                serverCarrierData?.billing_email || carrierData.billing_email
              }
              disabled={!!serverCarrierData?.billing_email}
            />
            {!serverCarrierData?.billing_email && (
              <span className="documents-billing-data__question-help">
                {t('emailHelp')}
              </span>
            )}
          </DocumentsFormQuestion>
          {(carrierData.is_autonomous || serverCarrierData.is_autonomous) && (
            <DocumentsFormQuestion
              name="fiscal-payment"
              extraClass="documents-form-question--radio"
            >
              <Radios
                size="M"
                legend={t('fiscalPayment')}
                name="fiscal_position"
                value={
                  serverCarrierData?.fiscal_position ||
                  carrierData.fiscal_position
                }
                items={[
                  { value: 'DIRECT', label: t('directEstimation') },
                  { value: 'IRPF_1', label: t('perModuleOrPercentage') },
                ]}
                onChange={(e, { name, value }) =>
                  handleFieldChange(name, value)
                }
                disabled={!!serverCarrierData?.fiscal_position}
              />
              {!serverCarrierData?.fiscal_position && (
                <span className="documents-billing-data__question-help">
                  {t('fiscalPaymentHelp')}
                </span>
              )}
            </DocumentsFormQuestion>
          )}
          {countrySpecificFields &&
            countrySpecificFields.map((field) => (
              <DynamicFormQuestion
                field={field}
                handleFieldChange={handleFieldChange}
                carrierData={carrierData}
                serverCarrierData={serverCarrierData}
                fieldsWithError={fieldsWithError}
              />
            ))}
          <div className="documents-billing-data__footer">
            <ButtonLoading
              className="documents-billing-data__submit"
              text={t('save')}
              handleClick={handleSubmit}
              skin="primary"
              loading={loadingForm}
              disabled={loadingForm}
              testId="documents-billing-data__submit"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
DocumentsBillingData.propTypes = {
  className: PropTypes.string,
};
export default DocumentsBillingData;
